import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layers, Search, X, ChevronLeft, ChevronRight, Copy, Check } from 'lucide-react';
import { adminApi } from '@/services/adminApi';
import type { AdminRenderQueueResponse, AdminRenderQueueStats } from '@/services/adminApi';
import { queryKeys } from '@/lib/queryKeys';
import type { RenderJobResponse } from '@printtat/shared';
import { PageHeader, PageContent, EmptyState, LoadingState, Button, IconButton, Table, Tabs } from '@printtat/ui';
import type { Column, SortState } from '@printtat/ui';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'processing', label: 'Processing' },
  { key: 'completed', label: 'Completed' },
  { key: 'failed', label: 'Failed' },
];

const STATUS_BADGE_STYLES: Record<string, string> = {
  pending: 'bg-yellow-50 text-yellow-700',
  processing: 'bg-blue-50 text-blue-700',
  completed: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-700',
};

const PRIORITY_LABELS: Record<number, string> = {
  20: 'Admin',
  10: 'Manual',
  1: 'Auto-save',
  0: 'Default',
};

const SOURCE_BADGE_STYLES: Record<string, string> = {
  user: 'bg-gray-50 text-gray-600',
  admin: 'bg-purple-50 text-purple-700',
};

const SOURCE_TABS = [
  { key: 'all', label: 'All Sources' },
  { key: 'user', label: 'User' },
  { key: 'admin', label: 'Admin' },
];

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (start: string, end: string | null) => {
  if (!end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

export const AdminRenderQueuePage = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ key: 'createdAt', order: 'desc' });
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null);
  const [detailJob, setDetailJob] = useState<RenderJobResponse | null>(null);
  const limit = 20;

  const { data: statsData } = useQuery<AdminRenderQueueStats>({
    queryKey: queryKeys.renderQueue.stats(),
    queryFn: () => adminApi.renderQueue.stats(),
    refetchInterval: 5000,
  });

  const { data, isLoading } = useQuery<AdminRenderQueueResponse>({
    queryKey: [...queryKeys.renderQueue.all(), { status: statusFilter, source: sourceFilter, search, page, limit, ...sort }],
    queryFn: () => adminApi.renderQueue.list({
      status: statusFilter === 'all' ? undefined : statusFilter,
      source: sourceFilter === 'all' ? undefined : sourceFilter,
      search: search || undefined,
      page,
      limit,
      sortBy: sort.key,
      sortOrder: sort.order,
    }),
    refetchInterval: 5000,
  });

  const jobs = data?.jobs ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const columns: Column<RenderJobResponse>[] = [
    {
      key: 'jobId',
      header: 'Job ID',
      render: (j) => (
        <span className="text-xs font-mono text-gray-600">
          {j.jobId.slice(0, 8)}...
        </span>
      ),
    },
    {
      key: 'surfaces',
      header: 'Surfaces',
      render: (j) => {
        if (!j.result?.surfaces?.length) {
          return <span className="text-xs text-gray-400">—</span>;
        }
        const previews = j.result.surfaces.filter((s) => s.previewImageUrl && s.source !== 'default_cdn');
        if (previews.length === 0) {
          return <span className="text-xs text-gray-400">—</span>;
        }
        const allUrls = previews.map((s) => s.masterImageUrl ?? s.previewImageUrl!);
        return (
          <div className="flex items-center gap-1">
            {previews.map((s, i) => (
              <button
                key={s.surfaceKey}
                type="button"
                onClick={() => setLightbox({ images: allUrls, index: i })}
                className="w-10 h-10 rounded border border-gray-200 overflow-hidden hover:border-gray-400 hover:shadow-sm transition-all cursor-pointer shrink-0"
              >
                <img
                  src={s.previewImageUrl!}
                  alt={s.surfaceKey}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (j) => (
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE_STYLES[j.status] ?? 'bg-gray-100 text-gray-600'}`}>
          {j.status === 'processing' && (
            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
          )}
          {j.status}
        </span>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      render: (j) => (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${SOURCE_BADGE_STYLES[j.source] ?? 'bg-gray-100 text-gray-600'}`}>
          {j.source === 'admin' ? 'Admin' : 'User'}
        </span>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      render: (j) => (
        <span className="text-sm text-gray-600">
          {PRIORITY_LABELS[j.priority] ?? `P${j.priority}`}
        </span>
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      render: (j) => (
        <span className="text-sm text-gray-500">
          {formatDuration(j.createdAt, j.completedAt)}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Submitted',
      sortable: true,
      render: (j) => (
        <span className="text-sm text-gray-500">{formatDate(j.createdAt)}</span>
      ),
    },
    {
      key: 'error',
      header: 'Error',
      render: (j) =>
        j.error ? (
          <span className="text-xs text-red-600 truncate max-w-[200px] block">
            {j.error}
          </span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
  ];

  return (
    <PageContent>
      <PageHeader
        icon={Layers}
        title="Render Queue"
        description="Monitor SudoMock render jobs"
      />

      {/* Stats cards */}
      {statsData && (
        <div className="mt-6 grid grid-cols-4 gap-4">
          <StatsCard label="Waiting" value={statsData.waiting} color="yellow" />
          <StatsCard label="Active" value={statsData.active} color="blue" />
          <StatsCard label="Completed" value={statsData.completed} color="green" />
          <StatsCard label="Failed" value={statsData.failed} color="red" />
        </div>
      )}

      <div className="mt-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Tabs
            tabs={STATUS_TABS}
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
            variant="pill"
          />
          <Tabs
            tabs={SOURCE_TABS}
            value={sourceFilter}
            onChange={(v) => { setSourceFilter(v); setPage(1); }}
            variant="pill"
          />
        </div>

        <div className="relative min-w-[260px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by job ID or design ID..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <LoadingState variant="skeleton" columns={1} rows={5} />
        ) : jobs.length === 0 ? (
          <EmptyState
            icon={Layers}
            title="No render jobs found"
            description={search || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Render jobs will appear here when users generate mockups'}
          />
        ) : (
          <>
            <Table
              columns={columns}
              data={jobs}
              keyExtractor={(j) => j.jobId}
              onRowClick={(j) => setDetailJob(j)}
              sort={sort}
              onSortChange={(s) => { setSort(s); setPage(1); }}
            />

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  Previous
                </Button>
                <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Job detail modal */}
      {detailJob && (
        <RenderJobDetailModal job={detailJob} onClose={() => setDetailJob(null)} />
      )}

      {/* Image lightbox overlay */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
        >
          <IconButton
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white"
          >
            <X size={20} />
          </IconButton>

          {lightbox.images.length > 1 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((prev) =>
                  prev
                    ? { ...prev, index: (prev.index - 1 + prev.images.length) % prev.images.length }
                    : null,
                );
              }}
              size="md"
              className="absolute left-4 bg-white/10 hover:bg-white/20 text-white"
            >
              <ChevronLeft size={24} />
            </IconButton>
          )}

          <img
            src={lightbox.images[lightbox.index]}
            alt="Surface render"
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          {lightbox.images.length > 1 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((prev) =>
                  prev
                    ? { ...prev, index: (prev.index + 1) % prev.images.length }
                    : null,
                );
              }}
              size="md"
              className="absolute right-4 bg-white/10 hover:bg-white/20 text-white"
            >
              <ChevronRight size={24} />
            </IconButton>
          )}

          {lightbox.images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-medium">
              {lightbox.index + 1} / {lightbox.images.length}
            </div>
          )}
        </div>
      )}
    </PageContent>
  );
};

const STATS_COLORS = {
  yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  green: 'bg-green-50 text-green-700 border-green-200',
  red: 'bg-red-50 text-red-700 border-red-200',
};

const CopyableField = ({ label, value, truncate = false }: { label: string; value: string; truncate?: boolean }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="mt-0.5 flex items-center gap-1.5">
        <span className={`text-sm font-mono text-gray-900 ${truncate ? 'truncate' : 'break-all'}`}>{value}</span>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
        >
          {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
        </button>
      </dd>
    </div>
  );
};

const DetailField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <dt className="text-xs font-medium text-gray-500">{label}</dt>
    <dd className="mt-0.5 text-sm text-gray-900">{children}</dd>
  </div>
);

const RenderJobDetailModal = ({ job, onClose }: { job: RenderJobResponse; onClose: () => void }) => {
  const previews = job.result?.surfaces?.filter((s) => s.previewImageUrl && s.source !== 'default_cdn') ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Render Job Details</h2>
          <IconButton size="sm" variant="ghost" onClick={onClose}>
            <X size={18} />
          </IconButton>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* IDs */}
          <CopyableField label="Job ID" value={job.jobId} truncate />
          <CopyableField label="Design ID" value={job.designId} truncate />
          <CopyableField label="Garment ID" value={job.garmentId} truncate />

          {/* Status & Meta */}
          <div className="grid grid-cols-3 gap-4">
            <DetailField label="Status">
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE_STYLES[job.status] ?? 'bg-gray-100 text-gray-600'}`}>
                {job.status}
              </span>
            </DetailField>
            <DetailField label="Source">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${SOURCE_BADGE_STYLES[job.source] ?? 'bg-gray-100 text-gray-600'}`}>
                {job.source === 'admin' ? 'Admin' : 'User'}
              </span>
            </DetailField>
            <DetailField label="Priority">
              {PRIORITY_LABELS[job.priority] ?? `P${job.priority}`}
            </DetailField>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <DetailField label="Attempts">{job.attempts}</DetailField>
            <DetailField label="Duration">
              {formatDuration(job.createdAt, job.completedAt)}
            </DetailField>
            <DetailField label="Submitted">{formatDate(job.createdAt)}</DetailField>
          </div>

          {job.completedAt && (
            <DetailField label="Completed">{formatDate(job.completedAt)}</DetailField>
          )}

          {job.fingerprint && (
            <CopyableField label="Fingerprint" value={job.fingerprint} truncate />
          )}

          {/* Rendered surfaces */}
          {previews.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Rendered Surfaces</p>
              <div className="flex gap-2 flex-wrap">
                {previews.map((s) => (
                  <div key={s.surfaceKey} className="text-center">
                    <img
                      src={s.previewImageUrl!}
                      alt={s.surfaceKey}
                      className="w-20 h-20 rounded-lg border border-gray-200 object-cover"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">{s.surfaceKey}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {job.error && (
            <div>
              <p className="text-xs font-medium text-red-500 mb-1">Error</p>
              <pre className="text-xs text-red-700 bg-red-50 rounded-lg p-3 whitespace-pre-wrap break-all font-mono">
                {job.error}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatsCard = ({ label, value, color }: { label: string; value: number; color: keyof typeof STATS_COLORS }) => (
  <div className={`rounded-lg border px-4 py-3 ${STATS_COLORS[color]}`}>
    <p className="text-xs font-medium opacity-70">{label}</p>
    <p className="text-2xl font-semibold">{value}</p>
  </div>
);
